import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { countOrACVFormatterMillionFixedOne } from '../../util/customFunctions';

/**
 * D3 Stacked Bar Chart for Forecast Trend (Paper 1)
 * Shows Won (bottom) + Most Likely (top) with target line
 */
const ForecastTrendChart = ({ data, quarterLabel, status, target }) => {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!data || !chartRef.current) return;

    const containerEl = chartRef.current;

    const draw = () => {
      const rect = containerEl.getBoundingClientRect();
      const outerWidth = Math.max(320, Math.floor(rect.width || 0));
      // Keep a stable height so the card layout stays consistent, but still scales with CSS.
      const outerHeight = 250;

      // Clear previous chart
      d3.select(containerEl).selectAll('*').remove();

      const margin = { top: 30, right: 30, bottom: 55, left: 60 };
      const width = Math.max(200, outerWidth - margin.left - margin.right);
      const height = Math.max(140, outerHeight - margin.top - margin.bottom);

      const root = d3
        .select(containerEl)
        .append('svg')
        .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
        .attr('preserveAspectRatio', 'xMidYMid meet')
        .style('width', '100%')
        .style('height', '100%')
        .style('display', 'block');

      const svg = root
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Filter data to only show weeks with values
      const validData = data.filter(d => d.won !== null || d.mostLikely !== null);

      // Format week ending date for display (YYYY-MM-DD format)
      const formatWeekEnding = (weekEnding) => {
        if (!weekEnding) return '';
        // If already in YYYY-MM-DD format, return as is
        if (typeof weekEnding === 'string' && weekEnding.match(/^\d{4}-\d{2}-\d{2}/)) {
          return weekEnding.substring(0, 10);
        }
        const date = new Date(weekEnding);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      // X Scale - use weekEnding as domain
      const xScale = d3
        .scaleBand()
        .domain(data.map(d => d.weekEnding || d.week))
        .range([0, width])
        .padding(0.2);

      // Y Scale
      const maxValue = Math.max(
        target || 0,
        d3.max(data, d => (d.won || 0) + (d.mostLikely || 0)) || 0
      ) * 1.1;

      const yScale = d3
        .scaleLinear()
        .domain([0, maxValue])
        .range([height, 0]);

      // Background color based on status
      const bgColor = status === 'elapsed' ? '#f5f5f5' : status === 'current' ? '#fff' : '#fff';
      svg
        .append('rect')
        .attr('x', -margin.left)
        .attr('y', -margin.top)
        .attr('width', width + margin.left + margin.right)
        .attr('height', height + margin.top + margin.bottom)
        .attr('fill', bgColor);

      // X Axis - show week numbers with dates below
      svg
        .append('g')
        .attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(xScale).tickSize(0).tickFormat(() => ''));

      // Add week numbers (0, 1, 2, 3, etc. - uses actual week number from data)
      data.forEach((d) => {
        const weekNum = d.week !== undefined ? d.week : d.weekLabel;
        const xPos = xScale(d.weekEnding || d.week) + xScale.bandwidth() / 2;

        // Week number
        svg
          .append('text')
          .attr('x', xPos)
          .attr('y', height + 15)
          .attr('text-anchor', 'middle')
          .style('font-size', '9px')
          .style('font-weight', 'bold')
          .text(weekNum);

        // Date in MM-DD format below
        const dateStr = formatWeekEnding(d.weekEnding || d.week);
        const mmdd = dateStr ? dateStr.substring(5) : ''; // Extract MM-DD from YYYY-MM-DD
        svg
          .append('text')
          .attr('x', xPos)
          .attr('y', height + 28)
          .attr('text-anchor', 'middle')
          .style('font-size', '8px')
          .style('fill', '#666')
          .text(mmdd);
      });

      // Y Axis - use global countOrACVFormatterfixedOne formatter
      svg
        .append('g')
        .call(d3.axisLeft(yScale).ticks(5).tickFormat(d => {
          // Fallback formatter
          return countOrACVFormatterMillionFixedOne(d, true);
        }))
        .selectAll('text')
        .style('font-size', '9px');

      // Grid lines
      svg
        .append('g')
        .attr('class', 'grid')
        .call(d3.axisLeft(yScale).ticks(5).tickSize(-width).tickFormat(''))
        .selectAll('line')
        .style('stroke', '#e0e0e0')
        .style('stroke-opacity', 0.7);

      svg.selectAll('.domain').remove();

      // Draw Won bars (bottom - green)
      svg
        .selectAll('.bar-won')
        .data(validData)
        .enter()
        .append('rect')
        .attr('class', 'bar-won')
        .attr('x', d => xScale(d.weekEnding || d.week))
        .attr('y', d => yScale(d.won || 0))
        .attr('width', xScale.bandwidth())
        .attr('height', d => height - yScale(d.won || 0))
        .attr('fill', '#70ad47');

      // Draw Most Likely bars (top - light green) - for all quarters
      svg
        .selectAll('.bar-likely')
        .data(validData.filter(d => d.mostLikely > 0))
        .enter()
        .append('rect')
        .attr('class', 'bar-likely')
        .attr('x', d => xScale(d.weekEnding || d.week))
        .attr('y', d => yScale((d.won || 0) + (d.mostLikely || 0)))
        .attr('width', xScale.bandwidth())
        .attr('height', d => height - yScale(d.mostLikely || 0))
        .attr('fill', '#c5e0b4');

      // Minimum bar height to display value label (in pixels)
      const minBarHeightForLabel = 15;
      const barWidth = xScale.bandwidth();

      // Display values on Won bars (if space available)
      svg
        .selectAll('.label-won')
        .data(validData.filter(d => {
          const wonHeight = height - yScale(d.won || 0);
          return d.won > 0 && wonHeight >= minBarHeightForLabel;
        }))
        .enter()
        .append('text')
        .attr('class', 'label-won')
        .attr('x', d => xScale(d.weekEnding || d.week) + barWidth / 2)
        .attr('y', d => yScale(d.won || 0) + (height - yScale(d.won || 0)) / 2 + 4)
        .attr('text-anchor', 'middle')
        .attr('fill', '#fff')
        .attr('font-size', '8px')
        .attr('font-weight', 'bold')
        .text(d => ((d.won || 0) / 1000000).toFixed(1));

      // Display values on Most Likely bars (if space available)
      svg
        .selectAll('.label-likely')
        .data(validData.filter(d => {
          const likelyHeight = height - yScale(d.mostLikely || 0);
          return d.mostLikely > 0 && likelyHeight >= minBarHeightForLabel;
        }))
        .enter()
        .append('text')
        .attr('class', 'label-likely')
        .attr('x', d => xScale(d.weekEnding || d.week) + barWidth / 2)
        .attr('y', d => {
          const likelyHeight = height - yScale(d.mostLikely || 0);
          return yScale((d.won || 0) + (d.mostLikely || 0)) + likelyHeight / 2 + 4;
        })
        .attr('text-anchor', 'middle')
        .attr('fill', '#4a7c31')
        .attr('font-size', '8px')
        .attr('font-weight', 'bold')
        .text(d => ((d.mostLikely || 0) / 1000000).toFixed(1));

      // Display total value above the stacked bar for all data points
      svg
        .selectAll('.label-total')
        .data(validData.filter(d => (d.won > 0 || d.mostLikely > 0)))
        .enter()
        .append('text')
        .attr('class', 'label-total')
        .attr('x', d => xScale(d.weekEnding || d.week) + barWidth / 2)
        .attr('y', d => {
          const total = (d.won || 0) + (d.mostLikely || 0);
          return yScale(total) - 5;
        })
        .attr('text-anchor', 'middle')
        .attr('fill', '#333')
        .attr('font-size', '9px')
        .attr('font-weight', 'bold')
        .text(d => {
          const total = (d.won || 0) + (d.mostLikely || 0);
          return (total / 1000000).toFixed(1);
        });

      // Target line (red)
      if (target > 0) {
        svg
          .append('line')
          .attr('x1', 0)
          .attr('x2', width)
          .attr('y1', yScale(target))
          .attr('y2', yScale(target))
          .attr('stroke', '#bf9000')
          .attr('stroke-width', 2);

        // Quota label with actual value
        svg
          .append('text')
          .attr('x', 5)
          .attr('y', yScale(target) - 5)
          .attr('fill', '#bf9000')
          .attr('font-size', '10px')
          .attr('font-weight', 'bold')
          .text(`Target: ${(target / 1000000).toFixed(1)}`);
      }

      // Get status label for title
      const getStatusText = (s) => {
        switch (s) {
          case 'elapsed':
            return '(Completed)';
          case 'current':
            return '(Current)';
          default:
            return '(Future)';
        }
      };

      // Title with status
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', -10)
        .attr('text-anchor', 'middle')
        .attr('font-size', '12px')
        .attr('font-weight', 'bold')
        .text(`ACV forecast for ${quarterLabel} ${getStatusText(status)}`);

      // X axis label
      svg
        .append('text')
        .attr('x', width / 2)
        .attr('y', height + 50)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .text('Week Ending');
    };

    draw();

    // Redraw when the card/container resizes so the SVG always fits.
    const ro = new ResizeObserver(() => {
      draw();
    });
    ro.observe(containerEl);

    return () => {
      ro.disconnect();
    };
  }, [data, quarterLabel, status, target]);

  return <div ref={chartRef} id="printChart" style={{ width: '100%', height: '100%' }} />;
};

export default ForecastTrendChart;